import React, { Key, Ref, useImperativeHandle, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { CustomFilter, EllipsisSpan, Form, Paging, Select, Table, Tag, usePaging } from '@maxtropy/components';
import { CircuitLabelType, circuitLabelTypeDisplay } from '@/api/circuit';
import {
  apiV2ComprehensiveLabelListPost,
  apiV2ComprehensiveLabelPagePost,
  V2ComprehensiveLabelPagePostRequest,
  V2ComprehensiveLabelPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { TabKey } from '@/pages/Circuit/LabelsManage';

interface AreaModalProps {
  areaIds?: number[];
}

export interface AreaModalRef {
  selectedRowKeys?: number[];
}

const columns = [
  {
    title: '标签名称',
    dataIndex: 'name',
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '标签类型',
    dataIndex: 'type',
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: CircuitLabelType) => {
      return (
        <EllipsisSpan
          value={
            <Tag border="solid" color={v === CircuitLabelType.PLATFORM ? 'info' : 'success'}>
              {circuitLabelTypeDisplay[v]}
            </Tag>
          }
        />
      );
    },
  },
];

type SearchParams = Omit<V2ComprehensiveLabelPagePostRequest, 'page' | 'size'>;

export type AreaItem = Exclude<V2ComprehensiveLabelPagePostResponse['list'], undefined>[number];

const AreaModal = React.forwardRef((props: AreaModalProps, ref: Ref<AreaModalRef>) => {
  const { areaIds } = props;
  const pagingInfo = usePaging();
  const [form] = Form.useForm();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number> | undefined>(areaIds || undefined);
  const [searchParams, setSearchParams] = useState<SearchParams>();

  const onReset = () => {
    setSearchParams({});
    form.resetFields();
    setPageOffset(1);
  };

  const onFinish = (val: SearchParams) => {
    setSearchParams(val);
    setPageOffset(1);
  };

  const onSelectChange = (rowKeys: Key[]) => {
    setSelectedRowKeys(rowKeys as number[]);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedRowKeys ?? [],
    onChange: onSelectChange,
  };

  const { data, loading: isLoading } = useRequest(
    () =>
      apiV2ComprehensiveLabelPagePost({
        page: pageOffset,
        size: pageSize,
        type: TabKey.DEVICE,
        ...searchParams,
      }).then(res => {
        setTotalCount(res.total!);
        return res.list;
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams],
    }
  );

  useImperativeHandle(ref, () => ({ selectedRowKeys }));

  const { data: labelList } = useRequest(() =>
    apiV2ComprehensiveLabelListPost({ id: TabKey.DEVICE }).then(res => {
      return res.list;
    })
  );

  // options
  const labelListOptions = useMemo(() => {
    return (labelList ?? []).map(item => ({
      label: item.name,
      value: item.id,
    }));
  }, [labelList]);

  return (
    <>
      <CustomFilter<SearchParams>
        form={form}
        onFinish={val => onFinish(val as SearchParams)}
        colSpan={8}
        onReset={onReset}
      >
        <Form.Item name="ids" label="标签名称">
          <Select
            style={{ width: '100%' }}
            placeholder="请选择标签名称"
            options={labelListOptions}
            showSearch
            optionFilterProp="label"
            mode="multiple"
            allowClear
          />
        </Form.Item>
      </CustomFilter>
      <Table
        scroll={{ y: 380 }}
        rowKey="id"
        loading={isLoading}
        showSelectedCount
        selectedCount={selectedRowKeys?.length ?? 0}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </>
  );
});

export default AreaModal;
